/**
 * Rota de Fidelização (pedido de 19/10, item 10 — Fase 3): regras de TELA da pista e do Master.
 *
 * Contrato com o backend (nomes exatos):
 * - GET /descontos/aplicaveis -> "rota": null | {ativa, etapa (1..3, a próxima a receber), de: 3,
 *   desconto_centavos_litro, valor_minimo_reais, ja_abasteceu_hoje, premio_reais, mensagem}.
 *   Em rota: a lista tem SÓ o item "rota_etapa_N" (padrão); já abasteceu hoje: lista vazia, sem
 *   desconto nenhum. Rota concluída com prêmio: item "rota_premio" (R$) entre os demais.
 * - POST /atendimentos/calcular -> "rota": null | {etapa_aplicada, avanca, valor_minimo_reais, aviso}.
 * - POST /atendimentos/{id}/confirmar -> "rota": null | {etapa_concluida, proxima_etapa,
 *   proximo_desconto_centavos, premio_liberado, premio_reais, mensagem}.
 * - POST /clientes/{id}/rota/inscrever (cliente sem nenhum abastecimento) -> {ok, rota}.
 * - Config por unidade e categoria: rota_etapa1_centavos, rota_etapa2_centavos,
 *   rota_etapa3_centavos, rota_premio_reais, rota_valor_minimo_reais (NULL = 0).
 * - Liga/desliga global: master_config.rota_fidelizacao_ativa (padrão desligado).
 *
 * O backend é a fonte da verdade: aqui só se decide o que a tela mostra, com textos sem emojis.
 */
import { ehRotaEtapa, ehRotaPremio } from "@/lib/descontosPista";

export { ehRotaEtapa, ehRotaPremio };

export const ROTA_TOTAL_ETAPAS = 3;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const centavos = (v) => Math.round(num(v) * 100);

/** "R$ 1.234,56" no padrão pt-BR (sem depender do formatador da tela). */
export function reais(v) {
  return num(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** "12 centavos/L" (aceita fração: "12,5 centavos/L"). */
export function centavosLitro(v) {
  return `${num(v).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} centavos/L`;
}

/**
 * Bloco "rota" do /descontos/aplicaveis quando o cliente está NAS ETAPAS (1 a 3) com a Rota ligada;
 * null fora disso (rota desligada, concluída ou cliente fora da Rota).
 */
export function rotaEmAndamento(descInfo) {
  const r = descInfo?.rota;
  if (!r || typeof r !== "object" || r.ativa === false) return null;
  const de = num(r.de) || ROTA_TOTAL_ETAPAS;
  const etapa = num(r.etapa);
  if (!(etapa >= 1 && etapa <= de)) return null;
  return r;
}

/** "Etapa N de 3". */
export function textoEtapa(rota) {
  const de = num(rota?.de) || ROTA_TOTAL_ETAPAS;
  return `Etapa ${num(rota?.etapa) || 1} de ${de}`;
}

/** Valor mínimo para avançar (0 quando não configurado). */
export function minimoRota(rota) {
  const m = num(rota?.valor_minimo_reais);
  return m > 0 ? m : 0;
}

/**
 * Valor digitado abaixo do mínimo da Rota (comparação em centavos, mesma base do progressivo:
 * R$ digitado, ou litros x preço de cadastro). Sem valor digitado ou sem mínimo: false.
 */
export function rotaAbaixoMinimo(rota, base) {
  const min = minimoRota(rota);
  if (!min || !(num(base) > 0)) return false;
  return centavos(base) < centavos(min);
}

/** Aviso da pista quando o valor fica abaixo do mínimo (o cliente recebe o desconto, não avança). */
export function avisoAbaixoMinimo(rota) {
  return `Abaixo do mínimo de ${reais(minimoRota(rota))}: não avança de etapa. O cliente recebe o desconto da Etapa ${num(rota?.etapa) || 1} mesmo assim.`;
}

const HORA_RE = /^\d{2}:\d{2}$/;
const hora = (v) => (typeof v === "string" && HORA_RE.test(v) ? v : null);
const nomeCurto = (v) => (typeof v === "string" && v.trim() ? v.trim().slice(0, 60) : null);

/**
 * Texto do 2º abastecimento no mesmo dia (sem nenhum desconto hoje). Usa rota.bloqueio quando vem
 * ({status, frentista_nome, hora, libera_as}): venda ainda NÃO finalizada (calculada, ou na fila
 * offline) mostra quem iniciou, a hora e quando deixa de contar se foi abandonada.
 */
export function textoSemDescontoHoje(rota) {
  const etapa = num(rota?.etapa) || 1;
  const b = rota?.bloqueio && typeof rota.bloqueio === "object" ? rota.bloqueio : null;
  const quem = nomeCurto(b?.frentista_nome);
  const h = hora(b?.hora);
  if (b && b.status === "calculado") {
    const origem = [h ? `iniciada às ${h}` : null, quem ? `por ${quem}` : null].filter(Boolean).join(" ");
    const libera = hora(b.libera_as);
    return `Este cliente tem hoje uma venda com o desconto da Rota de Fidelização ainda não finalizada${origem ? ` (${origem})` : ""}. Este abastecimento fica SEM desconto (nem da Rota nem outros).`
      + (libera ? ` Se aquela venda foi abandonada, ela deixa de contar às ${libera}.` : "")
      + ` Próxima etapa: Etapa ${etapa}.`;
  }
  const detalhe = [h ? `às ${h}` : null, quem ? `com ${quem}` : null].filter(Boolean).join(" ");
  return `O cliente já abasteceu hoje pela Rota de Fidelização${detalhe ? ` (${detalhe})` : ""}. Este abastecimento fica SEM desconto (nem da Rota nem outros). No primeiro abastecimento de amanhã ele recebe o desconto da Etapa ${etapa}.`;
}

/**
 * O frentista pode inscrever o cliente na Rota pela pista: Rota ligada, cliente sem nenhum
 * abastecimento (ex.: autocadastro pela web) e ainda fora da Rota.
 */
export function podeInscreverRota({ rotaLigada, cliente, descInfo }) {
  if (!rotaLigada || !cliente) return false;
  if (num(cliente.total_abastecimentos) !== 0) return false;
  if (cliente.rota_status) return false;
  if (descInfo?.rota) return false;
  return true;
}

/**
 * Padrão da lista vindo do backend: o item (objeto) ou só o id. Devolve o item da lista (ou null).
 */
export function resolvePadrao(data) {
  const d = data?.default;
  if (!d) return null;
  if (typeof d === "object") return d;
  return (data?.descontos || []).find((x) => x.id === d) || null;
}

/** Bloco "rota" do calcular -> {etapa, avanca, aviso} para a tela de confirmação (ou null). */
export function resumoRotaCalculo(calc) {
  const r = calc?.rota;
  if (!r || typeof r !== "object") return null;
  const etapa = num(r.etapa_aplicada);
  if (!etapa) return null;
  const avanca = !!r.avanca;
  let aviso = r.aviso ? String(r.aviso) : null;
  if (!avanca && !aviso) {
    aviso = `Abaixo do mínimo de ${reais(r.valor_minimo_reais)}: recebe o desconto da Etapa ${etapa} mas não avança.`;
  }
  const texto = avanca
    ? (etapa >= ROTA_TOTAL_ETAPAS
      ? `Ao confirmar, o cliente conclui a Etapa ${etapa} e completa a Rota.`
      : `Ao confirmar, o cliente conclui a Etapa ${etapa}.`)
    : null;
  return { etapa, avanca, aviso, texto };
}

/**
 * Mensagem do pós-venda/comprovante a partir do bloco "rota" do confirmar. Usa a mensagem do
 * backend; sem ela, monta a partir dos campos. null quando não há o que mostrar.
 */
export function mensagemRotaConfirmar(rota) {
  if (!rota || typeof rota !== "object") return null;
  if (rota.mensagem) return String(rota.mensagem);
  if (rota.premio_liberado) {
    return `No próximo abastecimento o prêmio de ${reais(rota.premio_reais)} estará disponível.`;
  }
  if (rota.proxima_etapa) {
    return `Próximo abastecimento: Etapa ${rota.proxima_etapa} com ${centavosLitro(rota.proximo_desconto_centavos)}.`;
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// Master: configuração por unidade e categoria (MasterDescontos / MasterAppDriver).

export const CAMPOS_ROTA_CENTAVOS = ["rota_etapa1_centavos", "rota_etapa2_centavos", "rota_etapa3_centavos"];
export const CAMPOS_ROTA_REAIS = ["rota_premio_reais", "rota_valor_minimo_reais"];
// Sem teto: vale o que o Master lançar (decisão do cliente, 24/10/2026), como no backend.

function numeroOuNulo(v) {
  if (v == null) return null;
  const txt = String(v).trim().replace(",", ".");
  if (txt === "") return null;
  const n = Number(txt);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Campos da Rota prontos para o PUT da config (número, vazio = null; NULL no banco = 0).
 * Erro quando algum valor não é número ou é negativo.
 */
export function normalizaRotaConfig(cfg) {
  const valores = {};
  for (const k of [...CAMPOS_ROTA_CENTAVOS, ...CAMPOS_ROTA_REAIS]) valores[k] = numeroOuNulo(cfg?.[k]);
  const todos = Object.values(valores);
  if (todos.some((v) => Number.isNaN(v) || (v != null && v < 0))) {
    return { erro: "Rota de Fidelização: informe números válidos e não negativos (descontos em centavos/L, prêmio e mínimo em R$).", valores };
  }
  return { erro: null, valores };
}

/** Resumo dos valores configurados (tela da Rota). NULL = 0. */
export function resumoConfigRota(cfg) {
  return {
    etapa1: num(cfg?.rota_etapa1_centavos),
    etapa2: num(cfg?.rota_etapa2_centavos),
    etapa3: num(cfg?.rota_etapa3_centavos),
    premio: num(cfg?.rota_premio_reais),
    minimo: num(cfg?.rota_valor_minimo_reais),
  };
}

// ---------------------------------------------------------------------------------------------
// Master: painel.

export const ETAPAS_LISTA = [
  ["1", "Etapa 1"], ["2", "Etapa 2"], ["3", "Etapa 3"], ["concluido", "Concluídos"], ["premio", "Prêmio disponível"],
];

/** "Etapa N" do painel = quem vai receber o desconto da etapa N (decisão do item 10). */
export function rotuloEtapaCliente(etapa) {
  const e = String(etapa ?? "");
  if (e === "concluido" || e === "concluida") return "Concluído";
  if (e === "premio") return "Prêmio disponível";
  if (/^\d+$/.test(e)) return `Etapa ${e}`;
  return e || "—";
}

/** Percentual do funil (0..100, inteiro) em relação aos inscritos. */
export function pctFunil(valor, inscritos) {
  const v = num(valor);
  const t = num(inscritos);
  if (!(t > 0)) return 0;
  return Math.max(0, Math.min(100, Math.round((v / t) * 100)));
}

/**
 * Funil de evolução a partir dos totais do painel ("Etapa N" = quem vai receber a etapa N):
 * ingressaram -> concluíram a Etapa 1 (estão na 2, na 3 ou concluíram) -> concluíram a Etapa 2 ->
 * concluíram a Rota -> usaram o prêmio. Percentuais sobre os inscritos.
 */
export function funilRota(t) {
  const inscritos = num(t?.inscritos);
  const e2 = num(t?.na_etapa_2);
  const e3 = num(t?.na_etapa_3);
  const conc = num(t?.concluidos);
  return [
    ["inscritos", "Ingressaram na Rota", inscritos],
    ["etapa1", "Concluíram a Etapa 1", e2 + e3 + conc],
    ["etapa2", "Concluíram a Etapa 2", e3 + conc],
    ["concluidos", "Concluíram a Rota", conc],
    ["premio_usado", "Usaram o prêmio", num(t?.premio_usado)],
  ].map(([chave, label, valor]) => ({ chave, label, valor, pct: pctFunil(valor, inscritos) }));
}

/** Frentistas com alerta (inscrições muito acima da média dos colegas), maior razão primeiro. */
export function frentistasEmAlerta(lista) {
  return (Array.isArray(lista) ? lista : []).filter((f) => !!f?.alerta)
    .sort((a, b) => num(b.razao) - num(a.razao));
}

/** "1,75x" (razão das inscrições contra a média dos colegas). */
export function textoRazao(v) {
  if (v == null || v === "" || !Number.isFinite(Number(v))) return "—";
  return `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`;
}

/** Dia 'AAAA-MM-DD' (dia de Brasília) ou ISO -> 'DD/MM/AAAA', sem deslocar o fuso. */
export function fmtDia(v) {
  if (!v) return "—";
  const txt = String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(txt);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(txt);
  return Number.isNaN(d.getTime()) ? txt : d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

/** "24/10/2026 09:40" no horário de Brasília (UTC-3, sem horário de verão) a partir de ISO. */
export function fmtDataHora(v) {
  if (!v) return "—";
  const t = new Date(String(v)).getTime();
  if (Number.isNaN(t)) return String(v);
  const br = new Date(t - 3 * 3600 * 1000).toISOString();
  return `${br.slice(8, 10)}/${br.slice(5, 7)}/${br.slice(0, 4)} ${br.slice(11, 16)}`;
}

export const CATEGORIA_LBL = { normal: "Normal", app_driver: "Motorista de App" };

/** Percentual (0..100) vindo do backend para texto "12,5%". */
export function textoPct(v) {
  return `${num(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}

// ---------------------------------------------------------------------------------------------
// Master: envio manual de SMS/WhatsApp aos clientes da Rota (verificação final da Fase 5, item 10:
// "quero poder enviar mensagens de whatsapp ou sms para esses clientes").
// POST /master/rota-fidelizacao/enviar/preview e /enviar {canal, texto, etapa?, unit_id?,
// categorias?, cliente_ids?}. Seleção = a do segmento "Rota de Fidelização" das campanhas (ativos,
// sem a recusa de ofertas, categoria, unidade; Rota desligada ou sem valores tira quem está nas
// etapas). Cada mensagem vai para o Histórico de envios das Campanhas ("Envios manuais da Rota").

/** campanha_id dos envios manuais no Histórico de envios (services/campaign_scheduler.ENVIO_ROTA_ID). */
export const ENVIO_ROTA_ID = "manual_rota";
export const ENVIO_ROTA_NOME = "Envios manuais da Rota";
/** Cópia de VARIAVEIS_ENVIO_ROTA do backend (mesma ordem). */
export const VARIAVEIS_ENVIO_ROTA = ["nome", "unidade", "etapa", "proximo_desconto", "premio"];
export const VARIAVEIS_ENVIO_ROTA_DESCRICAO = {
  nome: "nome do cliente",
  unidade: "unidade onde entrou na Rota",
  etapa: "próxima etapa (vazio para quem concluiu)",
  proximo_desconto: "desconto da próxima etapa, em centavos/L (ex.: 20,5)",
  premio: "prêmio em R$ (ex.: 25,00); saldo de quem concluiu ou o prêmio configurado",
};
export const LIMITE_SELECIONADOS_ROTA = 500;
export const TEXTO_MAX_ENVIO_ROTA = 1000;
/** Texto inicial (sem acentos: cabe num SMS). */
export const TEXTO_ENVIO_ROTA_PADRAO = "Ola {nome}! Obrigado por participar da Rota de Fidelizacao. Esperamos voce no posto!";

const VAR_RE_ENVIO = /\{([^{}]*)\}/g;

/**
 * Mesmas recusas do backend: vazio, mais de 1000 caracteres, emojis e variável desconhecida.
 * Devolve a mensagem de erro ou null. `temEmoji` vem de lib/mensagensAuto (mesma faixa do backend).
 */
export function validarTextoEnvioRota(texto, temEmoji) {
  const t = String(texto ?? "").trim();
  if (!t) return "Escreva a mensagem.";
  if (t.length > TEXTO_MAX_ENVIO_ROTA) return `A mensagem pode ter no máximo ${TEXTO_MAX_ENVIO_ROTA} caracteres.`;
  if (typeof temEmoji === "function" && temEmoji(t)) return "A mensagem não pode ter emojis.";
  const desconhecidas = [...new Set([...t.matchAll(VAR_RE_ENVIO)].map((m) => m[1]))]
    .filter((v) => !VARIAVEIS_ENVIO_ROTA.includes(v));
  if (desconhecidas.length) {
    return `Variável desconhecida: ${desconhecidas.map((v) => `{${v}}`).join(", ")}. `
      + `Use ${VARIAVEIS_ENVIO_ROTA.map((v) => `{${v}}`).join(", ")}`;
  }
  return null;
}

/**
 * Corpo do POST. Com clientes selecionados vão só os ids (qualquer etapa); sem seleção vai o filtro
 * da lista: etapa da aba, unidade do topo ("all" = todas) e as categorias marcadas.
 */
export function corpoEnvioRota({ selecionados = [], etapa, unitId, categorias, canal, texto }) {
  const base = { canal: canal || "sms", texto: String(texto ?? "").trim() };
  if (selecionados.length) return { ...base, cliente_ids: [...selecionados] };
  return {
    ...base,
    etapa: etapa || null,
    unit_id: unitId && unitId !== "all" ? unitId : null,
    categorias: Array.isArray(categorias) ? [...categorias] : ["normal", "app_driver"],
  };
}

/** "3 clientes selecionados" ou "Etapa 2 · Posto Centro" (público da janela e da confirmação). */
export function textoPublicoEnvio({ selecionados = 0, etapa, unidadeNome }) {
  if (selecionados > 0) return `${selecionados} ${selecionados === 1 ? "cliente selecionado" : "clientes selecionados"}`;
  const e = (ETAPAS_LISTA.find(([k]) => k === etapa) || [null, "Todos na Rota"])[1];
  return `${e} · ${unidadeNome || "Todas as unidades"}`;
}

/** Erro da API do envio: 400 com a lista de variáveis vira texto; 409 = outro envio saindo. */
export function erroEnvioRota(e, apiError) {
  const d = e?.response?.data;
  if (d && Array.isArray(d.variaveis_desconhecidas) && d.variaveis_desconhecidas.length) {
    return `${d.detail || "Variável desconhecida"}`;
  }
  return typeof apiError === "function" ? apiError(e) : String(d?.detail || e?.message || "Falha no envio");
}
